import { labelDraftContract } from "./label-drafts.contract";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { listSortShape } from "../../schemas/common";
import {
  connectionConfigSchema,
  dataSourceConnectionTypeSchema,
  parseConfigSchema,
  pullConfigSchema,
  MAX_DATA_SOURCE_LOAD_CONCURRENCY,
} from "@erp/shared-types";

export const DataSourceSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable().optional(),
  isSource: z.boolean(),
  lastRead: z.string().datetime().nullable(),
  isSink: z.boolean(),
  lastWrite: z.string().datetime().nullable(),
  connectionType: dataSourceConnectionTypeSchema.nullable(),
  connectionConfig: connectionConfigSchema.nullable(),
  /**
   * Credential used to READ this connection. Read and write are separate so a
   * connection that is both a source and a sink needs no single key carrying
   * both capabilities.
   */
  credentialId: z.string().uuid().nullable(),
  /** Credential used to WRITE to this connection; null means it cannot be written to. */
  writeCredentialId: z.string().uuid().nullable(),
});

export const DataSourceGroupSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  dataSources: z.array(DataSourceSchema),
});

export const ListDataSourcesResponseSchema = z.object({
  groups: z.array(DataSourceGroupSchema),
});

export const CreateDataSourceInputSchema = z.object({
  title: z.string().min(1),
  description: z.string().trim().max(2000).nullish(),
  groupId: z.string().uuid().optional(),
  newGroupTitle: z.string().min(1).optional(),
  isSource: z.boolean(),
  isSink: z.boolean(),
  // connectionType is derived from connectionConfig.type by the service.
  connectionConfig: connectionConfigSchema.nullish(),
  credentialId: z.string().uuid().nullish(),
  writeCredentialId: z.string().uuid().nullish(),
});

export const CreateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const UpdateDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
});

export const UpdateDataSourceInputSchema = CreateDataSourceInputSchema.extend({
  id: z.string().uuid(),
});

export const UpdateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const DeleteDataSourceInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupInputSchema = z.object({
  title: z.string().min(1),
});

export const DeleteDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupResponseSchema = z.object({
  group: DataSourceGroupSchema.omit({ dataSources: true }),
});

export const DataSourceLinkSchema = z.object({
  id: z.string().uuid(),
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  schemaName: z.string(),
  foreignSchemaName: z.string(),
  foreignTableName: z.string(),
  // The owning DataSource's connection type — lets pickers filter links by
  // connection kind (e.g. the Blob Storage adapter only offers blob-backed links).
  connectionType: dataSourceConnectionTypeSchema.nullable(),
  pullConfig: pullConfigSchema.nullable(),
  parseConfig: parseConfigSchema.nullable(),
  /** Whether the link's schema has a load transform, i.e. can be direct-loaded. */
  hasLoadTransform: z.boolean(),
});

export const ListDataSourceLinksResponseSchema = z.object({
  links: z.array(DataSourceLinkSchema),
});

// Result of syncing a link's ingest-table indexes to the schema's per-field
// index config (created missing indexes, dropped no-longer-expected ones).
export const SyncLinkIndexesResponseSchema = z.object({
  createdIndexes: z.array(z.string()),
  droppedIndexes: z.array(z.string()),
});

export const DataSourceLinkGroupSchema = z.object({
  sourceTitle: z.string(),
  links: z.array(DataSourceLinkSchema),
});

export const ListAllDataSourceLinksResponseSchema = z.object({
  groups: z.array(DataSourceLinkGroupSchema),
});

// A DataSource Link's foreignSchemaName / foreignTableName are lowercased and
// double-quoted into `CREATE TABLE "schema"."table"` DDL by the ingest layer.
// Postgres identifiers are capped at 63 bytes and start with a letter or
// underscore, so we require a plain unquoted identifier: this keeps the name
// legal and injection-safe.
export const MAX_FOREIGN_NAME_LENGTH = 63;
const FOREIGN_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** Whether `name` is a valid DataSource Link schema/table name. */
export function isValidForeignName(name: string): boolean {
  return (
    name.length > 0 &&
    name.length <= MAX_FOREIGN_NAME_LENGTH &&
    FOREIGN_NAME_PATTERN.test(name)
  );
}

const ForeignNameSchema = z
  .string()
  .min(1)
  .max(MAX_FOREIGN_NAME_LENGTH)
  .regex(FOREIGN_NAME_PATTERN, {
    message:
      "must start with a letter or underscore and contain only letters, numbers, and underscores",
  });

export const CreateDataSourceLinkInputSchema = z.object({
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: ForeignNameSchema,
  foreignTableName: ForeignNameSchema,
  pullConfig: pullConfigSchema.nullish(),
  parseConfig: parseConfigSchema.nullish(),
});

export const UpdateDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: ForeignNameSchema,
  foreignTableName: ForeignNameSchema,
  pullConfig: pullConfigSchema.nullish(),
  parseConfig: parseConfigSchema.nullish(),
});

export const DeleteDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
});

// Scopes of DataSource Link state validation — see the backend's
// DataSourceStateValidator for what each scope checks.
export const LinkValidationScopeSchema = z.enum([
  "schema",
  "link_config",
  "ingest_table",
  "mappings_vs_table",
  "fdw_mirror",
]);

export const LinkValidationIssueSchema = z.object({
  scope: LinkValidationScopeSchema,
  severity: z.enum(["error", "warning"]),
  // Stable machine-readable code, e.g. "missing_table", "type_mismatch",
  // "fdw_table_missing". The UI keys repair actions off these.
  code: z.string(),
  message: z.string(),
  columnName: z.string().optional(),
  // Human-readable expected/actual values for mismatch-style issues
  // (e.g. "NOT NULL" vs "nullable", or the expected vs actual default).
  expected: z.string().optional(),
  actual: z.string().nullable().optional(),
});

export const ValidateDataSourceLinkResponseSchema = z.object({
  // "invalid" iff any issue has severity "error"; warnings alone stay "ok".
  status: z.enum(["ok", "invalid"]),
  issues: z.array(LinkValidationIssueSchema),
  // Scopes that could not run (e.g. fdw_mirror when FDW isn't configured).
  skippedScopes: z.array(LinkValidationScopeSchema),
});

/**
 * Leave ingest rows whose values already match untouched, and out of the
 * transform: a reload then only re-derives the products of the rows that
 * changed. True unless given. Send false for a full load that must re-derive
 * every product, say after a mapping change or to repair a product a failed
 * transform left missing.
 */
const SkipUnchangedRowsSchema = z.boolean().optional();

/**
 * Experimental: load the file in SQL mode, merging the whole file into the
 * ingest table with set-based SQL and transforming only the rows that
 * changed (every row when skipUnchangedRows is false). False unless given.
 * The plan falls back to the chunked path, and records why, when the link or
 * its databases do not qualify.
 */
const SqlModeSchema = z.boolean().optional();

/** How a load ran: `chunked` upserts, or `sql` (see SqlModeSchema). */
export const DirectLoadModeSchema = z.enum(["chunked", "sql"]);

export const StartLinkLoadInputSchema = z.object({
  linkId: z.string().uuid(),
  /** Explicit source (blob URI); omit to load the link's most recent matching file. */
  sourceUri: z.string().min(1).optional(),
  chunkBytes: z.number().int().positive().optional(),
  parallelism: z.number().int().positive().max(64).optional(),
  skipUnchangedRows: SkipUnchangedRowsSchema,
  sqlMode: SqlModeSchema,
});

export const StartLinkLoadResponseSchema = z.object({
  loadId: z.string(),
});

export const ListLinkLoadsInputSchema = z.object({
  linkId: z.string().uuid(),
  limit: z.number().int().positive().max(100).default(20),
});

// "running" while any chunk is pending or running; then "cancelled" when the
// load was cancelled before some chunk finished, else succeeded / failed when
// every chunk agrees, partial when they do not.
export const DirectLoadStatusSchema = z.enum([
  "running",
  "succeeded",
  "partial",
  "failed",
  "cancelled",
]);

export const LinkLoadSchema = z.object({
  loadId: z.string(),
  sourceUri: z.string(),
  status: DirectLoadStatusSchema,
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  chunks: z.number().int(),
  chunksPending: z.number().int(),
  chunksRunning: z.number().int(),
  chunksSucceeded: z.number().int(),
  chunksFailed: z.number().int(),
  chunksCancelled: z.number().int(),
  rowsRead: z.number().int(),
  rowsFailed: z.number().int(),
  rowsTransformed: z.number().int(),
  rowsSkipped: z.number().int(),
  // The first failed chunk's error, or the planning error of a load that
  // never got a chunk (no file, several files, an unmapped transform input).
  error: z.string().nullable(),
  /** How the load ran; `chunked` for loads recorded before SQL mode. */
  mode: DirectLoadModeSchema,
  /** Why a load asked for in SQL mode ran chunked; null otherwise. */
  sqlModeDeclinedReason: z.string().nullable(),
});

export const ListLinkLoadsResponseSchema = z.object({
  loads: z.array(LinkLoadSchema),
  /** What startLinkLoad uses when chunkBytes / parallelism are omitted. */
  defaults: z.object({
    chunkBytes: z.number().int().positive(),
    parallelism: z.number().int().positive(),
  }),
});

export const PreviewDataSourceLoadInputSchema = z.object({
  dataSourceId: z.string().uuid(),
});

export const DataSourceLoadLinkSchema = z.object({
  linkId: z.string().uuid(),
  /** `<foreignSchemaName>.<foreignTableName>`. */
  label: z.string(),
  schemaName: z.string(),
  stepType: z.string(),
});

export const DataSourceLoadSkippedLinkSchema = z.object({
  linkId: z.string().uuid(),
  label: z.string(),
  schemaName: z.string(),
  reason: z.string(),
});

export const PreviewDataSourceLoadResponseSchema = z.object({
  /** The links a batch would load, in load order. */
  links: z.array(DataSourceLoadLinkSchema),
  /** The data source's other links and why a batch would skip them. */
  skipped: z.array(DataSourceLoadSkippedLinkSchema),
  /** Loads running anywhere in the organization; a batch refuses to start while any is. */
  runningLoadIds: z.array(z.string()),
  /** What startDataSourceLoad uses when the per-link settings are omitted. */
  defaults: z.object({
    chunkBytes: z.number().int().positive(),
    parallelism: z.number().int().positive(),
  }),
});

export const StartDataSourceLoadInputSchema = z.object({
  dataSourceId: z.string().uuid(),
  /**
   * Load only these of the data source's loadable links (still in the planned
   * order); omit to load every loadable link. A picked link the preview would
   * skip refuses the batch.
   */
  linkIds: z.array(z.string().uuid()).min(1).optional(),
  /** Links loaded at once; 1 (serial) unless given. */
  concurrency: z
    .number()
    .int()
    .positive()
    .max(MAX_DATA_SOURCE_LOAD_CONCURRENCY)
    .optional(),
  chunkBytes: z.number().int().positive().optional(),
  parallelism: z.number().int().positive().max(64).optional(),
  skipUnchangedRows: SkipUnchangedRowsSchema,
  sqlMode: SqlModeSchema,
});

export const StartDataSourceLoadResponseSchema = z.object({
  /** The batch workflow id: one per organization, so a second batch cannot start while one runs. */
  batchId: z.string(),
  /** This batch's run of that workflow. */
  runId: z.string(),
  /** In load order, each with the load id its link's history will show. */
  links: z.array(DataSourceLoadLinkSchema.extend({ loadId: z.string() })),
});

export const DirectLoadBatchLinkStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "partial",
  "failed",
  "cancelled",
]);

export const DirectLoadBatchSchema = z.object({
  batchId: z.string(),
  runId: z.string(),
  status: z.enum(["running", "succeeded", "failed", "cancelled", "terminated"]),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  /** Every link of the batch with where it is; empty when the batch did not complete. */
  links: z.array(
    z.object({
      linkId: z.string(),
      loadId: z.string(),
      /** `<foreignSchemaName>.<foreignTableName>`, or the link id when the link is gone. */
      label: z.string(),
      status: DirectLoadBatchLinkStatusSchema,
      error: z.string().nullable(),
    }),
  ),
});

export const OrganizationLoadSchema = LinkLoadSchema.extend({
  linkId: z.string(),
  /** `<foreignSchemaName>.<foreignTableName>`, or the link id when the link is gone. */
  linkLabel: z.string(),
  dataSourceTitle: z.string().nullable(),
});

/** Columns of the loads page the server sorts by; each is a column id there. */
export const DirectLoadSortBySchema = z.enum([
  "link",
  "loadId",
  "startedAt",
  "finishedAt",
  "duration",
  "status",
  "chunks",
  "rowsRead",
  "rowsFailed",
  "rowsTransformed",
  "rowsSkipped",
  "throughput",
]);

export const ListDirectLoadsInputSchema = z.object({
  limit: z.number().int().positive().max(200).default(50),
  offset: z.number().int().nonnegative().default(0),
  /** Case-insensitive match on the link label, load id, source file or error. */
  search: z.string().trim().max(200).optional(),
  /** Keep loads in any of these states; absent or empty keeps them all. */
  status: z.array(DirectLoadStatusSchema).max(5).optional(),
  /**
   * Absent: newest first. A running load's duration and throughput count to
   * now. A `sortBy` without a direction sorts descending.
   */
  ...listSortShape(DirectLoadSortBySchema),
});

export const ListDirectLoadsResponseSchema = z.object({
  /** The organization's latest batch, or null when it never ran one. */
  batch: DirectLoadBatchSchema.nullable(),
  /** One page of the organization's loads across every link. */
  loads: z.array(OrganizationLoadSchema),
  /** Loads matching the search and status filters, over every page. */
  total: z.number().int().nonnegative(),
  /** Loads running in the organization, whatever the filters. */
  running: z.number().int().nonnegative(),
});

export const ListDataSourceLoadBatchesInputSchema = z.object({
  dataSourceId: z.string().uuid(),
  limit: z.number().int().positive().max(100).default(20),
});

export const DataSourceLoadBatchSchema = z.object({
  /** The batch workflow's run id, which every load of the batch records. */
  batchId: z.string(),
  // "running" while the batch workflow runs or any of its loads does; then
  // "cancelled" when any load was cancelled, else succeeded / failed when
  // every load agrees, partial when they do not.
  status: z.enum(["running", "succeeded", "partial", "failed", "cancelled"]),
  /** The first link load's start. */
  startedAt: z.string().datetime().nullable(),
  /** The last link load's finish; null while the batch runs. */
  finishedAt: z.string().datetime().nullable(),
  /** finishedAt − startedAt; null while the batch runs. */
  durationMs: z.number().int().nullable(),
  /** Links loaded (so far, while running). */
  links: z.number().int(),
  rowsRead: z.number().int(),
  rowsFailed: z.number().int(),
  rowsTransformed: z.number().int(),
  rowsSkipped: z.number().int(),
});

export const ListDataSourceLoadBatchesResponseSchema = z.object({
  batches: z.array(DataSourceLoadBatchSchema),
});

const DirectLoadBatchSkippedLinkSchema = z.object({
  linkId: z.string(),
  label: z.string(),
  schemaName: z.string(),
  reason: z.string(),
});

/**
 * Where a planned link of a batch got to: its load's status, or `pending`
 * while the batch has not reached it, `not_started` when the batch stopped
 * before it did, `empty` when its file had no rows to load.
 */
export const DirectLoadBatchHistoryLinkStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "partial",
  "failed",
  "cancelled",
  "not_started",
  "empty",
]);

/**
 * `skipped`: the batch never started (a workflow start while a load ran).
 * `incomplete`: it stopped short of some link with no load cancelled
 * (terminated or failed). Otherwise the batch status rules.
 */
export const DirectLoadBatchHistoryStatusSchema = z.enum([
  "running",
  "succeeded",
  "partial",
  "failed",
  "cancelled",
  "incomplete",
  "skipped",
]);

export const DirectLoadBatchHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  /** The batch workflow's run id; null for a skipped batch. */
  runId: z.string().nullable(),
  dataSourceId: z.string().uuid(),
  dataSourceTitle: z.string(),
  /** `manual`: started from the app; `workflow`: by a Start Data Source Load step. */
  trigger: z.enum(["manual", "workflow"]),
  triggeredByName: z.string().nullable(),
  workflowRunId: z.string().nullable(),
  status: DirectLoadBatchHistoryStatusSchema,
  skippedReason: z.string().nullable(),
  /** When the batch was asked for. */
  requestedAt: z.string().datetime(),
  /** The first link load's start. */
  startedAt: z.string().datetime().nullable(),
  /** The last link load's finish; null while running. */
  finishedAt: z.string().datetime().nullable(),
  durationMs: z.number().int().nullable(),
  settings: z
    .object({
      concurrency: z.number().int(),
      chunkBytes: z.number().int(),
      parallelism: z.number().int(),
      skipUnchangedRows: z.boolean(),
      /** Asked for SQL mode; absent on batches recorded before it existed. */
      sqlMode: z.boolean().optional(),
    })
    .nullable(),
  /** Every planned link in load order. */
  links: z.array(
    z.object({
      linkId: z.string(),
      loadId: z.string(),
      label: z.string(),
      status: DirectLoadBatchHistoryLinkStatusSchema,
      startedAt: z.string().datetime().nullable(),
      finishedAt: z.string().datetime().nullable(),
      rowsRead: z.number().int(),
      rowsFailed: z.number().int(),
      rowsTransformed: z.number().int(),
      rowsSkipped: z.number().int(),
      error: z.string().nullable(),
      /** How the link's load ran; null before it has a ledger row. */
      mode: DirectLoadModeSchema.nullable(),
      /** Why the load ran chunked although SQL mode was asked for. */
      sqlModeDeclinedReason: z.string().nullable(),
    }),
  ),
  /** The data source's links the batch left out, and why. */
  skippedLinks: z.array(DirectLoadBatchSkippedLinkSchema),
  rowsRead: z.number().int(),
  rowsFailed: z.number().int(),
  rowsTransformed: z.number().int(),
  rowsSkipped: z.number().int(),
});

export const ListDirectLoadBatchHistoryInputSchema = z.object({
  /** Only this data source's batches; absent lists every data source's. */
  dataSourceId: z.string().uuid().optional(),
  limit: z.number().int().positive().max(100).default(20),
  offset: z.number().int().nonnegative().default(0),
});

export const ListDirectLoadBatchHistoryResponseSchema = z.object({
  batches: z.array(DirectLoadBatchHistoryEntrySchema),
  total: z.number().int().nonnegative(),
});

export const DataSourceLoadOverviewSchema = z.object({
  dataSourceId: z.string().uuid(),
  dataSourceTitle: z.string(),
  groupTitle: z.string().nullable(),
  /** When any of its links last finished a load, single or batch. */
  lastLoadedAt: z.string().datetime().nullable(),
  running: z.boolean(),
  /** Its most recent batch, started or skipped. */
  lastBatch: DirectLoadBatchHistoryEntrySchema.nullable(),
  /** Median end-to-end time of its recent batches that ran every link. */
  typicalDurationMs: z.number().int().nullable(),
  links: z.object({
    total: z.number().int(),
    /** Links a direct load refuses (no or unsupported load transform). */
    notLoadable: z.number().int(),
    /** Loadable links never loaded. */
    neverLoaded: z.number().int(),
    /** Loadable links by their latest load's status. */
    latest: z.record(DirectLoadStatusSchema, z.number().int()),
  }),
});

export const ListDataSourceLoadOverviewResponseSchema = z.object({
  dataSources: z.array(DataSourceLoadOverviewSchema),
});

export const CancelDirectLoadInputSchema = z.object({
  loadId: z.string().min(1),
});

export const CancelledResponseSchema = z.object({
  /** False when nothing was running to cancel. */
  cancelled: z.boolean(),
});

export const CancelAllDirectLoadsResponseSchema = z.object({
  batchCancelled: z.boolean(),
  loadsCancelled: z.number().int(),
});

export const dataSourcesContracts = {
  labelDrafts: labelDraftContract,
  list: oc.input(z.object({})).output(ListDataSourcesResponseSchema).route({
    summary: "List data sources by group",
    description:
      "Returns every data source connection grouped by data source group, with its read/write flags, connection config, credentials, and last read and write times.",
    method: "GET",
  }),
  listLinks: oc
    .input(z.object({ dataSourceId: z.string().uuid() }))
    .output(ListDataSourceLinksResponseSchema)
    .route({
      summary: "List links for a data source",
      description:
        "Returns the schema-to-table links of one data source, each with its linked schema, ingest table name, connection type, and pull and parse config.",
      method: "GET",
    }),
  listAllLinks: oc
    .input(z.object({}))
    .output(ListAllDataSourceLinksResponseSchema)
    .route({
      summary: "List all data source links",
      description:
        "Returns every data source link across all data sources, grouped by source title, with schema, ingest table, connection type, and pull and parse config.",
      method: "GET",
    }),
  createLink: oc
    .input(CreateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({
      summary: "Link a schema to a data source",
      description:
        "Creates a link mapping a schema onto a named ingest table for a data source, with optional pull and parse config. Extension schemas are rejected; names are lowercased.",
      method: "POST",
    }),
  updateLink: oc
    .input(UpdateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({
      summary: "Update a data source link",
      description:
        "Changes a link's schema, ingest table name, and, when sent, its pull and parse config. Returns the updated link.",
      method: "POST",
    }),
  deleteLink: oc
    .input(DeleteDataSourceLinkInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a data source link",
      description:
        "Drops the link's ingest table and its data, then deletes the link and its product data links. Blocked while any workflow step references the link.",
      method: "POST",
    }),
  createGroup: oc
    .input(CreateDataSourceGroupInputSchema)
    .output(CreateDataSourceGroupResponseSchema)
    .route({
      summary: "Create a data source group",
      description:
        "Creates an empty named group for organizing data sources and returns it.",
      method: "POST",
    }),
  deleteGroup: oc
    .input(DeleteDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a data source group",
      description:
        "Deletes an empty data source group; fails while the group still contains data sources.",
      method: "POST",
    }),
  create: oc
    .input(CreateDataSourceInputSchema)
    .output(CreateDataSourceResponseSchema)
    .route({
      summary: "Create a data source",
      description:
        "Creates a data source connection in an existing or new group, with source/sink flags, connection config, and separate read and write credentials. Returns any newly created group.",
      method: "POST",
    }),
  update: oc
    .input(UpdateDataSourceInputSchema)
    .output(UpdateDataSourceResponseSchema)
    .route({
      summary: "Update a data source",
      description:
        "Updates a data source's title, group (existing or new), source/sink flags, connection config, and read and write credentials. Returns any newly created group.",
      method: "POST",
    }),
  delete: oc
    .input(DeleteDataSourceInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a data source",
      description:
        "Drops every linked ingest table and its data, then deletes the data source and its links. Blocked while any workflow step references one of its links.",
      method: "POST",
    }),
  updateGroup: oc
    .input(UpdateDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Move a data source to another group",
      description:
        "Reassigns one data source to a different existing data source group.",
      method: "POST",
    }),
  validateLink: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(ValidateDataSourceLinkResponseSchema)
    .route({
      summary: "Validate a data source link",
      description:
        "Checks a link's schema, config, ingest table, column mappings, and mirror table for drift. Returns ok or invalid with error/warning issues and any skipped checks.",
      method: "GET",
    }),
  createLinkTable: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Create a link's ingest table",
      description:
        "Creates the ingest database table for a data source link from its schema's fields and key columns. Fails if the table cannot be created.",
      method: "POST",
    }),
  // (Re)creates the app-DB FDW mirror of the link's ingest table — the repair
  // action for fdw_* validation issues.
  syncLinkFdwMirror: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Repair a link's mirror table",
      description:
        "Recreates the app database's foreign-table mirror of a link's ingest table, fixing mirror issues reported by link validation.",
      method: "POST",
    }),
  // Creates/drops indexes on the link's ingest table to match the schema's
  // per-field index config — the repair action for missing_index/extra_index
  // validation issues.
  syncLinkIndexes: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(SyncLinkIndexesResponseSchema)
    .route({
      summary: "Sync a link's ingest table indexes",
      description:
        "Creates missing and drops unexpected indexes on a link's ingest table to match the schema's per-field index settings. Returns the created and dropped index names.",
      method: "POST",
    }),
  // Direct load: chunked parse → ingest upsert → load transform, on the
  // Temporal ingest lane. Bypasses CDC; see docs/data-ingest/direct-load.md.
  startLinkLoad: oc
    .input(StartLinkLoadInputSchema)
    .output(StartLinkLoadResponseSchema)
    .route({
      summary: "Start a direct load of a data source link",
      description:
        "Starts loading a link's file (an explicit blob URI, or the link's most recent matching file) in byte-range chunks: each chunk is parsed, upserted into the ingest table and run through the schema's load transform into the app. Returns the load id.",
      method: "POST",
    }),
  listLinkLoads: oc
    .input(ListLinkLoadsInputSchema)
    .output(ListLinkLoadsResponseSchema)
    .route({
      summary: "List direct loads of a data source link",
      description:
        "Returns the link's most recent direct loads, newest first, each with its source, status, start and finish times, chunk counts by status, and rows read, failed, transformed and skipped.",
      method: "GET",
    }),
  previewDataSourceLoad: oc
    .input(PreviewDataSourceLoadInputSchema)
    .output(PreviewDataSourceLoadResponseSchema)
    .route({
      summary: "Preview a batch direct load of a data source",
      description:
        "Returns which of the data source's links a batch load would load, in order (item master first, then other product schemas, then the rest), which it would skip and why, and the loads currently running that would block it.",
      method: "GET",
    }),
  startDataSourceLoad: oc
    .input(StartDataSourceLoadInputSchema)
    .output(StartDataSourceLoadResponseSchema)
    .route({
      summary: "Start a batch direct load of a data source",
      description:
        "Starts a direct load of every loadable link of the data source, one link at a time unless a higher concurrency is given, each link as its own load with its own history. Refuses while any direct load is running in the organization. Returns the batch id and the planned loads.",
      method: "POST",
    }),
  listDirectLoads: oc
    .input(ListDirectLoadsInputSchema)
    .output(ListDirectLoadsResponseSchema)
    .route({
      summary: "List the organization's direct loads",
      description:
        "Returns the organization's latest batch load (with every link's progress while it runs) and one page of its direct loads across every link: searched, filtered by status and sorted on the server, newest first by default, with the matching total and the number of loads running.",
      method: "GET",
    }),
  listDataSourceLoadBatches: oc
    .input(ListDataSourceLoadBatchesInputSchema)
    .output(ListDataSourceLoadBatchesResponseSchema)
    .route({
      summary: "List a data source's batch direct loads",
      description:
        "Returns the data source's most recent batch loads (Load all, manual or scheduled), newest first, each with its status, its first load's start and last load's finish, end-to-end duration, links loaded, and rows read, failed, transformed and skipped. Batches started before batch ids were recorded on the load ledger are not listed.",
      method: "GET",
    }),
  listDirectLoadBatchHistory: oc
    .input(ListDirectLoadBatchHistoryInputSchema)
    .output(ListDirectLoadBatchHistoryResponseSchema)
    .route({
      summary: "List the organization's batch direct loads",
      description:
        "Returns one page of the organization's batch loads (Load all, from the app or a workflow), newest first, optionally of one data source: each with who started it, its status (including skipped batches that never started), its end-to-end time, every planned link with where it got to (including links a cancelled batch never reached), the links it left out and why, and its row totals.",
      method: "GET",
    }),
  listDataSourceLoadOverview: oc
    .input(z.object({}))
    .output(ListDataSourceLoadOverviewResponseSchema)
    .route({
      summary: "Summarize direct loading per data source",
      description:
        "Returns, for every source data source with links in the organization, when it was last loaded, its most recent batch, the median duration of its recent complete batches, and how its links' latest loads ended (including links never loaded and links that cannot be loaded).",
      method: "GET",
    }),
  cancelDirectLoad: oc
    .input(CancelDirectLoadInputSchema)
    .output(CancelledResponseSchema)
    .route({
      summary: "Cancel a direct load",
      description:
        "Cancels one running direct load. Its unfinished chunks are recorded as cancelled; if it belongs to a batch, the batch records it and moves on to the next link.",
      method: "POST",
    }),
  cancelDirectLoadBatch: oc
    .input(z.object({}))
    .output(CancelledResponseSchema)
    .route({
      summary: "Cancel the organization's batch load",
      description:
        "Cancels the running batch load. Its running link loads are cancelled with it; links not yet started never get a load.",
      method: "POST",
    }),
  cancelAllDirectLoads: oc
    .input(z.object({}))
    .output(CancelAllDirectLoadsResponseSchema)
    .route({
      summary: "Cancel every running direct load",
      description:
        "Cancels the running batch load, if any, and every direct load with a chunk still pending or running.",
      method: "POST",
    }),
} as const;

export type DataSource = z.infer<typeof DataSourceSchema>;
export type DataSourceGroup = z.infer<typeof DataSourceGroupSchema>;
export type ListDataSourcesResponse = z.infer<
  typeof ListDataSourcesResponseSchema
>;
export type CreateDataSourceInput = z.infer<typeof CreateDataSourceInputSchema>;
export type UpdateDataSourceInput = z.infer<typeof UpdateDataSourceInputSchema>;
export type CreateDataSourceGroupInput = z.infer<
  typeof CreateDataSourceGroupInputSchema
>;
export type DataSourceLink = z.infer<typeof DataSourceLinkSchema>;
export type SyncLinkIndexesResponse = z.infer<
  typeof SyncLinkIndexesResponseSchema
>;
export type DataSourceLinkGroup = z.infer<typeof DataSourceLinkGroupSchema>;
export type ListAllDataSourceLinksResponse = z.infer<
  typeof ListAllDataSourceLinksResponseSchema
>;
export type CreateDataSourceLinkInput = z.infer<
  typeof CreateDataSourceLinkInputSchema
>;
export type UpdateDataSourceLinkInput = z.infer<
  typeof UpdateDataSourceLinkInputSchema
>;
export type LinkValidationScope = z.infer<typeof LinkValidationScopeSchema>;
export type LinkValidationIssue = z.infer<typeof LinkValidationIssueSchema>;
export type ValidateDataSourceLinkResponse = z.infer<
  typeof ValidateDataSourceLinkResponseSchema
>;
export type LinkLoad = z.infer<typeof LinkLoadSchema>;
export type DataSourceLoadLink = z.infer<typeof DataSourceLoadLinkSchema>;
export type DataSourceLoadSkippedLink = z.infer<
  typeof DataSourceLoadSkippedLinkSchema
>;
export type PreviewDataSourceLoadResponse = z.infer<
  typeof PreviewDataSourceLoadResponseSchema
>;
export type StartLinkLoadInput = z.infer<typeof StartLinkLoadInputSchema>;
export type StartDataSourceLoadInput = z.infer<
  typeof StartDataSourceLoadInputSchema
>;
export type DirectLoadBatch = z.infer<typeof DirectLoadBatchSchema>;
export type DirectLoadStatus = z.infer<typeof DirectLoadStatusSchema>;
export type DirectLoadSortBy = z.infer<typeof DirectLoadSortBySchema>;
export type DataSourceLoadBatch = z.infer<typeof DataSourceLoadBatchSchema>;
export type OrganizationLoad = z.infer<typeof OrganizationLoadSchema>;
export type DirectLoadBatchHistoryEntry = z.infer<
  typeof DirectLoadBatchHistoryEntrySchema
>;
export type DataSourceLoadOverview = z.infer<
  typeof DataSourceLoadOverviewSchema
>;
export type ListDirectLoadsInput = z.input<typeof ListDirectLoadsInputSchema>;
export type ListDirectLoadsResponse = z.infer<
  typeof ListDirectLoadsResponseSchema
>;
